import { AsyncThunkAction } from 'actions/types'
import { DisableIncognitoAction } from 'actions/user/incognitoUserAction'
import {
  promoPositions,
  RatingPromo,
} from 'components/page/PhotoRating/PhotoRating.constants'
import { findRandomItem } from 'functions/array'
import { RootState } from 'reducers/RootState'

export const createAvailableTypes = (
  featuredPhotosActive: boolean | null,
  advEnabled: boolean
): RatingPromo[] => {
  const result: RatingPromo[] = []
  if (!featuredPhotosActive) {
    result.push(RatingPromo.featured)
  }
  if (advEnabled) {
    result.push(RatingPromo.banner)
  }
  return result
}

/**
 * Расставляем промо для интервала.
 * Происходит при старте, либо достижения конца интервала.
 * https://redmine.mamba.ru/issues/93173
 */
export const setUpPromoAction =
  (isColdStart?: boolean): AsyncThunkAction =>
  (dispatch, getState) => {
    const state = getState()

    /**
     * Когда мы перезагружаем страницу или возвращаемся, мы восстанавливаем state
     * из localStorage, то нам не нужно заменять promo из localStorage.
     * Обновлять promo только в процессе голосования.
     * https://redmine.mamba.ru/issues/120252
     */
    if (
      (isColdStart && !state?.photoRatingReducer?.currentPromoIndex) ||
      !isColdStart
    ) {
      dispatch(clearPromoAction())

      const promoIndex = findRandomItem(promoPositions)
      const promoType = getPromoType(state)

      dispatch(setCurrentPromoAndIndexAction(promoIndex, promoType))
    }
  }

const getAvailablePromoTypes = (state: RootState) => {
  const {
    photoRatingReducer: { featuredPhotosActive },
    bannerReducer: { enabled, directAdFox },
  } = state

  return createAvailableTypes(
    featuredPhotosActive,
    enabled && directAdFox.includes('rating_swipe')
  )
}

const getPromoType = (state: RootState) => {
  const {
    photoRatingReducer: { currentPromo },
  } = state

  const availableTypes = getAvailablePromoTypes(state)

  return selectPromoType(availableTypes, currentPromo)
}

const selectPromoType = (
  promoTypes: RatingPromo[],
  lastShownPromoType: RatingPromo | null
) => {
  if (lastShownPromoType && promoTypes.length > 1) {
    return (
      promoTypes.find((promoType) => promoType !== lastShownPromoType) ||
      promoTypes[0]
    )
  }

  return findRandomItem(promoTypes) || null
}

export const SET_RATING_PROMO = 'SET_RATING_PROMO'

interface SetCurrentPromoAndIndexAction {
  type: typeof SET_RATING_PROMO
  promo: RatingPromo
  index: number
}

export const setCurrentPromoAndIndexAction =
  (index: number, promo: RatingPromo | null): AsyncThunkAction =>
  (dispatch) => {
    if (promo) {
      return dispatch({
        type: SET_RATING_PROMO,
        index,
        promo,
      })
    }
  }

export const INCREMENT_VOTED_COUNTER = 'INCREMENT_VOTED_COUNTER'

interface IncrementVotedCounterAction {
  type: typeof INCREMENT_VOTED_COUNTER
}

export const incrementVotedCounterAction = () => ({
  type: INCREMENT_VOTED_COUNTER,
})

export const INCREMENT_LIKED_COUNTER = 'INCREMENT_LIKED_COUNTER' as const

/** Считаем только по лайкам */
export const incrementLikedCounterAction = () => ({
  type: INCREMENT_LIKED_COUNTER,
})

export const UPDATE_LAST_INCOGNITO_NOTICE_SHOW =
  'UPDATE_LAST_INCOGNITO_NOTICE_SHOW' as const

export const updateLastIncognitoNoticeShownAction = () => ({
  type: UPDATE_LAST_INCOGNITO_NOTICE_SHOW,
})

export const skipPromoAction = (): AsyncThunkAction => (dispatch) => {
  return dispatch(incrementVotedCounterAction())
}

export const SET_FEATURED_ACTIVE_PHOTOS = 'SET_FEATURED_ACTIVE_PHOTOS' as const

/**
 * Указывает, находятся ли фото показов в ротации.
 * @param value
 */
export const setFeaturedActivePhotosAction = (value: boolean) => ({
  type: SET_FEATURED_ACTIVE_PHOTOS,
  value,
})

export const CLEAR_PROMO = 'CLEAR_PROMO' as const

export const clearPromoAction = () => ({
  type: CLEAR_PROMO,
})

export type PhotoRatingPromoActionTypes =
  | SetCurrentPromoAndIndexAction
  | IncrementVotedCounterAction
  | ReturnType<typeof incrementLikedCounterAction>
  | ReturnType<typeof updateLastIncognitoNoticeShownAction>
  | ReturnType<typeof setFeaturedActivePhotosAction>
  | DisableIncognitoAction
  | ReturnType<typeof clearPromoAction>
